/* eslint-disable no-underscore-dangle */
import type {ImageProps, ObjectEvents, SerializedImageProps, TClassProperties, TOptions} from '@postermywall/fabricjs-2';
import {classRegistry, FabricImage} from '@postermywall/fabricjs-2';

export const spriteDefaultValues: Partial<TClassProperties<Sprite>> = {};

interface UniqueSpriteProps {
  frameTime: number;
  frameImages: HTMLImageElement[];
}

export interface SpriteProps extends ImageProps, UniqueSpriteProps {}

export class Sprite<
    Props extends TOptions<SpriteProps> = Partial<SpriteProps>,
    SProps extends SerializedImageProps = SerializedImageProps,
    EventSpec extends ObjectEvents = ObjectEvents,
  >
  extends FabricImage<Props, SProps, EventSpec>
  implements SpriteProps
{
  declare frameTime: number;
  declare frameImages: HTMLImageElement[];

  private currentFrameIndex = 0;
  private paused = true;
  private animationInterval?: NodeJS.Timeout;

  static override type = 'Sprite';

  static override ownDefaults = spriteDefaultValues;

  static override getDefaults(): Record<string, any> {
    return {
      ...super.getDefaults(),
      ...Sprite.ownDefaults,
    };
  }

  /**
   * Constructor
   */
  constructor(imageElement: HTMLImageElement, options: Props) {
    if (options.frameTime === undefined || options.frameImages === undefined) {
      throw new Error('options should be given the frameTime and frameImages');
    }

    super(imageElement, options);
    Object.assign(this, Sprite.ownDefaults);
    this.setOptions(options);

    this.animationInterval = setInterval((): void => {
      if (!this.paused) {
        this.currentFrameIndex += 1;
        if (this.currentFrameIndex === this.frameImages.length) {
          this.currentFrameIndex = 0;
        }
        this.setElement(this.frameImages[this.currentFrameIndex]);
      }
    }, this.frameTime);
  }

  public async play(): Promise<void> {
    return new Promise((resolve) => {
      if (this.paused) {
        this.paused = false;
      }
      resolve();
    });
  }

  public async stop(): Promise<void> {
    return new Promise((resolve) => {
      this.paused = true;
      this.currentFrameIndex = 0;
      resolve();
    });
  }

  public async pause(): Promise<void> {
    return new Promise((resolve) => {
      this.paused = true;
      resolve();
    });
  }

  public isPaused(): boolean {
    return this.paused;
  }

  public getCurrentTime(): number {
    return Math.round((((this.currentFrameIndex * this.frameTime) / 1000) * 1000) / 1000);
  }

  public async seek(time: number): Promise<void> {
    return new Promise((resolve) => {
      const spriteIndex = Math.floor((time * 1000) / this.frameTime);
      if (spriteIndex < this.frameImages.length) {
        this.currentFrameIndex = spriteIndex;
        this.setElement(this.frameImages[this.currentFrameIndex]);
      }
      resolve();
    });
  }

  public onRemove(): void {
    clearInterval(this.animationInterval);
    this.animationInterval = undefined;
  }

}

classRegistry.setClass(Sprite);
